import React from 'react';
import { Shield, Eye, Lock, Users, Database, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function Privacy() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Shield className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.privacy.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.privacy.subtitle}
            </p>
            <p className="text-sm text-gray-500 mt-4">
              {t.privacy.lastUpdated}: 19 januari 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 text-green-600 mr-3" />
                Inleiding
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Welkom bij Heitje ("wij", "ons", "onze"). Wij zijn toegewijd aan het beschermen van uw privacy en het transparant zijn over hoe wij uw persoonlijke gegevens behandelen. Dit privacybeleid beschrijft hoe wij informatie verzamelen, gebruiken, delen en beschermen wanneer u onze diensten gebruikt.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Door gebruik te maken van de Heitje app en diensten, stemt u in met de praktijken die in dit beleid worden beschreven.
              </p>
            </div>

            {/* Data Collection */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Database className="h-6 w-6 text-green-600 mr-3" />
                Welke gegevens verzamelen wij
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Persoonlijke informatie</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Naam en contactgegevens (e-mailadres, telefoonnummer)</li>
                <li>Adresgegevens voor ophaalservice</li>
                <li>Betalingsinformatie voor statiegeld uitbetalingen</li>
                <li>Profielinformatie en voorkeuren</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Gebruiksgegevens</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>App-gebruiksstatistieken en interacties</li>
                <li>Locatiegegevens (met uw toestemming) voor het vinden van inzamelpunten</li>
                <li>Verzamel- en ophaalgeschiedenis</li>
                <li>Communicatie met onze klantenservice</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Technische gegevens</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Apparaatinformatie (type, besturingssysteem, unieke identificatoren)</li>
                <li>IP-adres en netwerkgegevens</li>
                <li>Cookies en vergelijkbare technologieën</li>
                <li>Crashlogs en prestatiegegevens</li>
              </ul>
            </div>

            {/* Data Usage */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-green-600 mr-3" />
                Hoe wij uw gegevens gebruiken
              </h2>
              
              <p className="text-gray-600 mb-4">Wij gebruiken uw persoonlijke gegevens voor de volgende doeleinden:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Dienstverlening</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Het faciliteren van blikjesverzameling en ophaalafspraken</li>
                <li>Verwerking van statiegeld uitbetalingen</li>
                <li>Klantenservice en ondersteuning</li>
                <li>Het matchen van gebruikers met verzamelaars in hun gebied</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Verbetering van diensten</h3>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Analyseren van gebruikspatronen om onze app te verbeteren</li>
                <li>Ontwikkelen van nieuwe functies en diensten</li>
                <li>Optimaliseren van ophaalroutes en -tijden</li>
                <li>Personaliseren van uw ervaring</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Communicatie</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Verzenden van belangrijke updates over uw account</li>
                <li>Meldingen over ophaalafspraken en betalingen</li>
                <li>Marketing communicatie (met uw toestemming)</li>
                <li>Onderzoeken en feedback verzoeken</li>
              </ul>
            </div>

            {/* Data Sharing */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-green-600 mr-3" />
                Delen van gegevens
              </h2>
              
              <p className="text-gray-600 mb-4">Wij delen uw persoonlijke gegevens alleen in de volgende gevallen:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Met uw toestemming</h3>
              <p className="text-gray-600 mb-6">
                Wij delen uw gegevens met derden wanneer u daar expliciet toestemming voor heeft gegeven, bijvoorbeeld bij het delen van contactgegevens met een verzamelaar voor een ophaalafspraak.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Serviceproviders</h3>
              <p className="text-gray-600 mb-6">
                Wij werken samen met vertrouwde serviceproviders die ons helpen bij het leveren van onze diensten, zoals betalingsverwerkers, cloudopslag providers en analysediensten. Deze partners hebben alleen toegang tot de gegevens die nodig zijn voor hun specifieke diensten.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wettelijke vereisten</h3>
              <p className="text-gray-600 mb-6">
                Wij kunnen uw gegevens delen wanneer dit wettelijk vereist is, bijvoorbeeld bij gerechtelijke bevelen, overheidsonderzoeken of om onze rechten te beschermen.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Bedrijfsoverdrachten</h3>
              <p className="text-gray-600">
                In het geval van een fusie, overname of verkoop van activa, kunnen uw gegevens worden overgedragen aan de nieuwe eigenaar, waarbij uw privacy rechten behouden blijven.
              </p>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-green-600 mr-3" />
                Gegevensbeveiliging
              </h2>
              
              <p className="text-gray-600 mb-4">
                Wij nemen de beveiliging van uw persoonlijke gegevens zeer serieus en implementeren verschillende technische en organisatorische maatregelen:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Encryptie van gegevens tijdens overdracht en opslag</li>
                <li>Regelmatige beveiligingsaudits en penetratietests</li>
                <li>Toegangscontroles en authenticatie voor medewerkers</li>
                <li>Secure hosting bij gecertificeerde cloudproviders</li>
                <li>Regelmatige back-ups en disaster recovery procedures</li>
                <li>Training van medewerkers over gegevensbescherming</li>
              </ul>
              
              <p className="text-gray-600">
                Hoewel wij alle redelijke maatregelen nemen om uw gegevens te beschermen, kunnen wij geen absolute beveiliging garanderen. Wij raden u aan om sterke wachtwoorden te gebruiken en uw account gegevens vertrouwelijk te houden.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-green-600 mr-3" />
                Uw rechten
              </h2>
              
              <p className="text-gray-600 mb-4">
                Onder de Algemene Verordening Gegevensbescherming (AVG) heeft u verschillende rechten betreffende uw persoonlijke gegevens:
              </p>
              
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Recht op toegang</h3>
                  <p className="text-gray-600 text-sm">U heeft het recht om te weten welke persoonlijke gegevens wij over u verwerken en een kopie daarvan te ontvangen.</p>
                </div>
                
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Recht op rectificatie</h3>
                  <p className="text-gray-600 text-sm">U kunt verzoeken om onjuiste of onvolledige persoonlijke gegevens te corrigeren of aan te vullen.</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Recht op verwijdering</h3>
                  <p className="text-gray-600 text-sm">In bepaalde gevallen kunt u verzoeken om uw persoonlijke gegevens te laten verwijderen ("recht om vergeten te worden").</p>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Recht op beperking</h3>
                  <p className="text-gray-600 text-sm">U kunt verzoeken om de verwerking van uw persoonlijke gegevens te beperken in bepaalde omstandigheden.</p>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Recht op overdraagbaarheid</h3>
                  <p className="text-gray-600 text-sm">U heeft het recht om uw persoonlijke gegevens in een gestructureerd, gangbaar formaat te ontvangen.</p>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Recht van bezwaar</h3>
                  <p className="text-gray-600 text-sm">U kunt bezwaar maken tegen de verwerking van uw persoonlijke gegevens voor bepaalde doeleinden.</p>
                </div>
              </div>
              
              <p className="text-gray-600 mt-6">
                Om gebruik te maken van deze rechten, kunt u contact met ons opnemen via de contactgegevens onderaan dit beleid. Wij zullen binnen 30 dagen reageren op uw verzoek.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies en tracking</h2>
              
              <p className="text-gray-600 mb-4">
                Wij gebruiken cookies en vergelijkbare technologieën om uw ervaring te verbeteren en onze diensten te optimaliseren:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Essentiële cookies</h3>
              <p className="text-gray-600 mb-4">
                Deze cookies zijn noodzakelijk voor het functioneren van onze app en kunnen niet worden uitgeschakeld. Ze omvatten sessie-identificatie en beveiligingscookies.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Analytische cookies</h3>
              <p className="text-gray-600 mb-4">
                Wij gebruiken analytische cookies om te begrijpen hoe gebruikers onze app gebruiken, zodat wij deze kunnen verbeteren. Deze gegevens worden geanonimiseerd.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Marketing cookies</h3>
              <p className="text-gray-600 mb-4">
                Met uw toestemming gebruiken wij marketing cookies om u relevante advertenties te tonen en de effectiviteit van onze marketingcampagnes te meten.
              </p>
              
              <p className="text-gray-600">
                U kunt uw cookie voorkeuren beheren via de instellingen in onze app of via uw browser instellingen.
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Bewaarperiode</h2>
              
              <p className="text-gray-600 mb-4">
                Wij bewaren uw persoonlijke gegevens alleen zolang als nodig is voor de doeleinden waarvoor ze zijn verzameld:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Accountgegevens:</strong> Zolang uw account actief is, plus 3 jaar na deactivatie</li>
                <li><strong>Transactiegegevens:</strong> 7 jaar voor boekhoudkundige en fiscale doeleinden</li>
                <li><strong>Communicatie:</strong> 3 jaar na laatste contact voor klantenservice doeleinden</li>
                <li><strong>Marketing gegevens:</strong> Tot u zich afmeldt of 2 jaar na laatste interactie</li>
                <li><strong>Analytische gegevens:</strong> Geanonimiseerd na 2 jaar</li>
              </ul>
            </div>

            {/* International Transfers */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Internationale overdrachten</h2>
              
              <p className="text-gray-600 mb-4">
                Uw persoonlijke gegevens worden voornamelijk verwerkt binnen de Europese Economische Ruimte (EER). In sommige gevallen kunnen gegevens worden overgedragen naar landen buiten de EER, bijvoorbeeld voor cloudopslag of analysediensten.
              </p>
              
              <p className="text-gray-600 mb-4">
                Wanneer wij gegevens overdragen naar landen buiten de EER, zorgen wij ervoor dat:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Het ontvangende land een adequaat beschermingsniveau heeft volgens de Europese Commissie</li>
                <li>Wij passende waarborgen implementeren, zoals standaard contractuele clausules</li>
                <li>In specifieke situaties, uw expliciete toestemming wordt verkregen</li>
              </ul>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy van minderjarigen</h2>
              
              <p className="text-gray-600 mb-4">
                Onze diensten zijn bedoeld voor gebruikers van 16 jaar en ouder. Wij verzamelen niet bewust persoonlijke gegevens van kinderen onder de 16 jaar zonder toestemming van ouders of voogden.
              </p>
              
              <p className="text-gray-600">
                Als u ontdekt dat een kind onder de 16 jaar persoonlijke gegevens aan ons heeft verstrekt, neem dan contact met ons op zodat wij deze gegevens kunnen verwijderen.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Wijzigingen in dit beleid</h2>
              
              <p className="text-gray-600 mb-4">
                Wij kunnen dit privacybeleid van tijd tot tijd bijwerken om wijzigingen in onze praktijken of om wettelijke redenen weer te geven. Belangrijke wijzigingen zullen wij u meedelen via:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
                <li>Een melding in onze app</li>
                <li>E-mail naar uw geregistreerde e-mailadres</li>
                <li>Een prominente melding op onze website</li>
              </ul>
              
              <p className="text-gray-600">
                Wij raden u aan dit beleid regelmatig te controleren om op de hoogte te blijven van hoe wij uw gegevens beschermen.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-green-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Mail className="h-6 w-6 text-green-600 mr-3" />
                Contact
              </h2>
              
              <p className="text-gray-600 mb-6">
                Als u vragen heeft over dit privacybeleid of over hoe wij uw persoonlijke gegevens behandelen, kunt u contact met ons opnemen:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Heitje B.V.</h3>
                  <p className="text-gray-600 text-sm mb-1">Recycling Innovation Center</p>
                  <p className="text-gray-600 text-sm mb-1">Duurzaamheidslaan 123</p>
                  <p className="text-gray-600 text-sm mb-3">1000 AB Amsterdam</p>
                  <p className="text-gray-600 text-sm">Nederland</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Contactgegevens</h3>
                  <p className="text-gray-600 text-sm mb-1">
                    <strong>E-mail:</strong> privacy@heitje.nl
                  </p>
                  <p className="text-gray-600 text-sm mb-1">
                    <strong>Telefoon:</strong> +31 20 123 4567
                  </p>
                  <p className="text-gray-600 text-sm mb-3">
                    <strong>KvK nummer:</strong> 12345678
                  </p>
                  <p className="text-gray-600 text-sm">
                    <strong>BTW nummer:</strong> NL123456789B01
                  </p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-green-200">
                <h3 className="font-semibold text-gray-900 mb-2">Functionaris Gegevensbescherming</h3>
                <p className="text-gray-600 text-sm">
                  Voor specifieke privacy gerelateerde vragen kunt u ook direct contact opnemen met onze Functionaris Gegevensbescherming via: <a href="mailto:dpo@heitje.nl" className="text-green-600 hover:text-green-700">dpo@heitje.nl</a>
                </p>
              </div>
              
              <div className="mt-6 pt-6 border-t border-green-200">
                <h3 className="font-semibold text-gray-900 mb-2">Klacht indienen</h3>
                <p className="text-gray-600 text-sm">
                  Als u niet tevreden bent met hoe wij uw klacht behandelen, heeft u het recht om een klacht in te dienen bij de Nederlandse Autoriteit Persoonsgegevens (AP): <a href="https://autoriteitpersoonsgegevens.nl" className="text-green-600 hover:text-green-700" target="_blank" rel="noopener noreferrer">autoriteitpersoonsgegevens.nl</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}